import { useActiveConversation } from '../../chat/ActiveConversationContext';
import { useTranslation } from '../../../lib/i18n';
import { useProductName } from '../../../lib/use-brand';
import { useOpenSideChat } from '../../../lib/use-open-side-chat';
import type { PresenceAction } from '../../../lib/presence-checklist';
import type { I18nCopy } from '../../../lib/space-resources';
import type { WorkspaceWebsite } from '../../../lib/workspace-website';
import { websiteChatContext } from '../../../lib/workspace-website';
import { presenceSpaceChatContext } from '../../../lib/presence';
import {
  boundConversationFor,
  expectSiteChat,
  openSitePane,
  openSiteEditFlow,
  requestComposerDraft,
  type SiteSpace,
} from '../../../lib/site-edit-store';

/** The site's address for an edit seed: its url, else its name. */
export function siteRefOf(website: WorkspaceWebsite | null): string {
  return (website?.url ?? '').trim() || (website?.name ?? '').trim();
}

/**
 * Opens and fills the Space's chat for the site flows: a SENT message (the hand-over by GitHub, the move, a republish
 * request, the edit seed) or a DRAFT the owner sends (the hand-over ZIP). Every new chat is stamped
 * with the Space; a sent one carries the website's hidden context too, as the presence actions do.
 */
export function useSiteChat(space: SiteSpace) {
  const { t } = useTranslation();
  const productName = useProductName();
  const openSideChat = useOpenSideChat();
  const { openMaestroChat, setActiveConversationId, setChatMode } = useActiveConversation();
  const text = (copy: I18nCopy) => t(copy.key, { productName, ...copy.values });
  const context = () =>
    space.website
      ? websiteChatContext(space.workspaceId, space.workspaceName, space.website)
      : presenceSpaceChatContext(space.workspaceId, space.workspaceName);

  /** Sends `copy` in a NEW chat of the Space, or into `conversationId` when given. */
  const send = (copy: I18nCopy, conversationId?: string | null) => {
    if (conversationId) openSideChat({ kind: 'existing', conversationId, message: text(copy) });
    else openSideChat({ kind: 'new', message: text(copy), context: context(), workspaceId: space.workspaceId });
  };

  /** SENDS `message` as the owner's own words into `conversationId`, else into a NEW chat of the Space, and shows it. */
  const sendText = (message: string, conversationId: string | null) => {
    if (conversationId) openSideChat({ kind: 'existing', conversationId, message });
    else openSideChat({ kind: 'new', message, context: context(), workspaceId: space.workspaceId });
  };

  /** Puts `message` in the composer of `conversationId` (else a new chat of the Space) without sending it. */
  const draft = (message: string, conversationId: string | null, attach = false) => {
    if (conversationId) {
      setActiveConversationId(conversationId);
      setChatMode('expanded');
    } else {
      openMaestroChat();
    }
    requestComposerDraft({ text: message, conversationId, workspaceId: space.workspaceId, ...(attach ? { attach: true } : {}) });
  };

  return { send, sendText, draft, text };
}

/**
 * What a presence action does beyond a url (which its button opens itself) — the site flows:
 *   - a Symphony site's Edit chat: the usual seed, and the chat is BOUND to the site (its "Preview site" link, a
 *     phone review's Finish sending to it); on a computer the preview pane opens beside it;
 *   - TAKEOVER / MOVE: their offer; a phone's "Review site": the full-screen review.
 * Returns false for a target it does not handle (a plain chat is the caller's, as before).
 */
export function useSiteEditRunner(space: SiteSpace) {
  const { send } = useSiteChat(space);
  return (action: PresenceAction): boolean => {
    const target = action.target;
    switch (target.kind) {
      case 'chat':
        if (!target.site) return false;
        expectSiteChat(space.workspaceId, target.site.previewUrl);
        if (target.site.pane) openSitePane({ workspaceId: space.workspaceId, previewUrl: target.site.previewUrl, conversationId: null });
        send(target.message);
        return true;
      case 'takeover':
        openSiteEditFlow({ kind: 'takeover', ...space });
        return true;
      case 'move':
        openSiteEditFlow({ kind: 'move', offer: target.offer, ...space });
        return true;
      case 'review':
        openSiteEditFlow({ kind: 'review', previewUrl: target.previewUrl, ...space });
        return true;
      default:
        return false;
    }
  };
}

export { boundConversationFor };
